import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import LandingPage from '@/pages/LandingPage'
import HomePage from '@/pages/HomePage'
import ReservePage from '@/pages/ReservePage'
import KioskPage from '@/pages/KioskPage'
import { AuthGuard } from '@/components/AuthGuard'
import { useBranding } from '@/hooks/useBranding'

const PAGE_TITLES: Record<string, string> = {
  '/': 'Sign in',
  '/home': 'Reservations',
  '/reserve': 'Book parking',
  '/kiosk': 'Kiosk',
}

/**
 * Each page names itself in the tab and to a screen reader, under the
 * operator's name where the brand is theirs: "Book parking · USC Reservations".
 * The shell's alone; a module never retitles its host's page.
 */
function PageTitle() {
  const { pathname } = useLocation()
  const { brand } = useBranding()
  useEffect(() => {
    const site = `${brand.powered_by ? brand.name : 'Parkeze'} Reservations`
    const page = PAGE_TITLES[pathname]
    document.title = page ? `${page} · ${site}` : site
  }, [pathname, brand.powered_by, brand.name])
  return null
}

/**
 * reserve.parkeze.com is a shell (logbook decision 21): sign-in, routing and
 * /home are its own; /reserve and /kiosk are the `reservation-app` and
 * `reservation-desk` modules, mounted from modules.parkeze.com (src/shell).
 * Their sources live in this repo too -- src/embed and what it imports -- but
 * the shell does not compile them in.
 *
 * It has no guest pages. A guest's link is go.parkeze.com's (decision 25);
 * public/_redirects sends the old /r/* and /vehicle/* links there.
 */
export default function App() {
  return (
    <ErrorBoundary>
      <PageTitle />
      <a href="#app-main-content" className="skip-link">
        Skip to main content
      </a>
      <div id="app-main-content" tabIndex={-1}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/home"
            element={
              <AuthGuard>
                <HomePage />
              </AuthGuard>
            }
          />
          <Route
            path="/reserve"
            element={
              <AuthGuard>
                <ReservePage />
              </AuthGuard>
            }
          />
          <Route
            path="/kiosk"
            element={
              <AuthGuard>
                <KioskPage />
              </AuthGuard>
            }
          />
          <Route path="/my-reservations" element={<Navigate to="/home" replace />} />
        </Routes>
      </div>
    </ErrorBoundary>
  )
}
